/**
 * Sprint 22 — ChatRail.
 *
 * The cockpit chat's third-column wrapper. Two states (chat is always available,
 * so unlike Sprint 9a's RightRail it never collapses to 0):
 *  - expanded  → 380px panel hosting ChatPanel (which owns its own header)
 *  - collapsed → 40px strip (the `]` shortcut toggles)
 *
 * Expanded state is persisted per-workspace by ChatRailContext.
 *
 * ## №45 — ONE TOGGLE, ONE POSITION, ON THE SEAM
 *
 * David: *"the open/close control must sit in the SAME place whether the panel is
 * open or shut, so it reads as one button that toggles rather than two that
 * appear in different places."*
 *
 * It used to be two genuinely different controls. Collapsed rendered its own
 * strip button here (`ti-message-circle`, centred in a 40px strip); expanded
 * delegated to `ChatPanel`'s header, whose FIRST child was a `ti-chevron-right`
 * button — a different element, in a different container, at a different offset.
 * Nothing about them said "these are the same control", because they were not.
 *
 * The toggle is now hoisted out of both branches into ONE persistently-positioned
 * button anchored to the panel's left seam. It is a sibling of whatever the rail
 * is currently showing, absolutely positioned at the coordinates the expanded
 * header button already occupied (`left: 6, top: 7, 30×30` — see `seamToggle`),
 * so the expanded surface does not move at all and the collapsed one comes to
 * meet it.
 *
 * **`expanded` decides its icon and its label. It never decides its position.**
 * The icon still changes with state, deliberately: a chat glyph when shut says
 * what the strip opens, and a chevron when open says which way it goes. That is
 * the same control speaking, not two controls — which is exactly the distinction
 * the row draws.
 */

import { type CSSProperties } from "react";
import { useLocation } from "react-router";
import { useChatRail } from "./ChatRailContext";
import { ChatPanel } from "./ChatPanel";

export function ChatRail() {
  const { expanded, toggleExpanded } = useChatRail();
  const location = useLocation();

  // Sprint 23.5 — the Hire wizard surface IS the chat for that workflow. The
  // right-rail chat would compete for attention with the wizard's conversation
  // panel, so hide it entirely (and don't reserve the 380px column underneath
  // the wizard backdrop). The AppShell column collapses to nothing because
  // ChatRail returns null here.
  if (location.pathname === "/hire") return null;

  return (
    <div style={railRoot} data-testid="chat-rail">
      {/* №45 — the ONE toggle. Rendered before the branch and positioned by
          `seamToggle` alone, so no state can move it. */}
      <button
        type="button"
        onClick={toggleExpanded}
        aria-label={expanded ? "Collapse chat" : "Open chat"}
        title={expanded ? "Collapse chat (])" : "Open chat (])"}
        style={seamToggle}
        data-testid="chat-rail-toggle"
      >
        <i
          className={`ti ti-${expanded ? "chevron-right" : "message-circle"}`}
          style={{ fontSize: expanded ? 16 : 18 }}
          aria-hidden="true"
        />
      </button>

      {expanded ? (
        <aside style={panel} aria-label="Chat">
          <ChatPanel />
        </aside>
      ) : (
        // The strip is now chrome only — its affordance moved to the seam above.
        <div style={collapsedStrip} />
      )}
    </div>
  );
}

/** Positioning context for the seam toggle. Fills the column; paints nothing. */
const railRoot: CSSProperties = {
  position: "relative",
  width: "100%",
  height: "100%",
};

/**
 * The one position. These are `ChatPanel`'s own header numbers — `headerRow` is
 * 44px tall with `padding: 0 6px` and `iconBtn` is 30×30, so `left: 6` and
 * `top: 7` put this exactly where that header's first button sat. The expanded
 * surface therefore does not move by a pixel; only the collapsed affordance does,
 * which is the point.
 */
const seamToggle: CSSProperties = {
  position: "absolute",
  left: 6,
  top: 7,
  zIndex: 2,
  width: 30,
  height: 30,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  borderRadius: "var(--border-radius-sm)",
};

const panel: CSSProperties = {
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderLeft: "var(--kuvio-border-default)",
  background: "var(--color-background-primary)",
  overflow: "hidden",
  fontFamily: "var(--font-sans)",
};

const collapsedStrip: CSSProperties = {
  width: "100%",
  height: "100%",
  display: "flex",
  justifyContent: "center",
  paddingTop: 8,
  borderLeft: "var(--kuvio-border-default)",
  background: "var(--color-background-primary)",
};

/* №45 — `stripButton` DELETED, not left unused. Removals are deliverables:
 * the collapsed strip has no button of its own any more, and a dead style block
 * is how the second control finds its way back. */
